import React, { useState, useEffect } from "react";
import MainCalculatorsList from "../FoodCalculator/MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Kalkulator Konversi Ukuran Loyang Kue - Sesuaikan Jumlah Bahan";
const DESCRIPTION =
  "Kalkulator gratis untuk mengonversi jumlah kue antara loyang yang berbeda. Masukkan ukuran loyang asli dan baru, lalu dapatkan rasio penyesuaian untuk semua bahan dengan tepat.";
const KEYWORDS =
  "kalkulator loyang kue, konversi loyang kue, sesuaikan resep kue, ukuran loyang kue, kalkulator memanggang, cake pan conversion calculator, konversi ukuran kue, loyang bulat persegi panjang";
const OG_URL = "https://do-calculate.com/calculator/id/cake-pan-conversion/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Kalkulator Konversi Ukuran Loyang Kue",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Beranda", item: "https://do-calculate.com/" },
      { "@type": "ListItem", position: 2, name: "Kalkulator", item: "https://do-calculate.com/id/all-calculators/" },
      { "@type": "ListItem", position: 3, name: "Kalkulator Konversi Ukuran Loyang Kue", item: OG_URL },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Bagaimana cara mengonversi resep kue dari loyang bulat ke persegi panjang?",
        acceptedAnswer: { "@type": "Answer", text: "Hitung luas loyang asli dan baru, lalu bagi luas loyang baru dengan loyang asli untuk mendapatkan rasio penyesuaian. Kalikan setiap bahan dalam resep dengan rasio ini." },
      },
      {
        "@type": "Question",
        name: "Apa yang terjadi jika menggunakan loyang lebih besar tanpa menyesuaikan jumlah?",
        acceptedAnswer: { "@type": "Answer", text: "Kue akan lebih tipis dan mungkin kering lebih cepat, karena adonan akan menyebar ke area yang lebih luas. Selalu sesuaikan jumlah atau waktu memanggang saat mengganti ukuran loyang." },
      },
      {
        "@type": "Question",
        name: "Apakah suhu berubah saat mengganti ukuran loyang?",
        acceptedAnswer: { "@type": "Answer", text: "Suhu biasanya tetap sama, tetapi waktu memanggang berubah. Loyang lebih besar atau lebih dalam membutuhkan waktu lebih lama, dan yang lebih kecil membutuhkan waktu lebih singkat. Periksa kematangan dengan tusuk gigi." },
      },
    ],
  },
];

const PAN_SHAPES = {
  round: "Bulat",
  square: "Persegi",
  rectangular: "Persegi Panjang",
  bundt: "Bundt / Cincin",
  springform: "Springform Bulat",
};

const calcPanArea = (shape, d1, d2, depth) => {
  const h = parseFloat(depth) || 0;
  switch (shape) {
    case "round":
    case "springform": { const r = parseFloat(d1) / 2; return Math.PI * r * r * h; }
    case "square": { const s = parseFloat(d1); return s * s * h; }
    case "rectangular": { return parseFloat(d1) * parseFloat(d2) * h; }
    case "bundt": { const R = parseFloat(d1) / 2; const r = R * 0.35; return Math.PI * (R * R - r * r) * h; }
    default: return 0;
  }
};

function CakePanCalculator() {
  const [origShape, setOrigShape] = useState("round");
  const [origD1, setOrigD1] = useState("");
  const [origD2, setOrigD2] = useState("");
  const [origDepth, setOrigDepth] = useState("");
  const [newShape, setNewShape] = useState("square");
  const [newD1, setNewD1] = useState("");
  const [newD2, setNewD2] = useState("");
  const [newDepth, setNewDepth] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const needsD2 = (shape) => shape === "rectangular";

  const getD1Label = (shape) => {
    if (shape === "round" || shape === "springform" || shape === "bundt") return "Diameter (cm)";
    if (shape === "square") return "Panjang Sisi (cm)";
    return "Panjang (cm)";
  };

  const calculateResult = () => {
    try {
      setError("");
      const oD1 = parseFloat(origD1); const oD2 = needsD2(origShape) ? parseFloat(origD2) : 0;
      const oDepth = parseFloat(origDepth); const nD1 = parseFloat(newD1);
      const nD2 = needsD2(newShape) ? parseFloat(newD2) : 0; const nDepth = parseFloat(newDepth);

      if (!oD1 || !oDepth || !nD1 || !nDepth) { setResult(null); return; }
      if (needsD2(origShape) && !oD2) { setResult(null); return; }
      if (needsD2(newShape) && !nD2) { setResult(null); return; }
      if (oD1 <= 0 || nD1 <= 0 || oDepth <= 0 || nDepth <= 0) { setError("Masukkan nilai positif yang valid"); setResult(null); return; }

      const origVolume = calcPanArea(origShape, oD1, oD2, oDepth);
      const newVolume = calcPanArea(newShape, nD1, nD2, nDepth);

      if (origVolume <= 0 || newVolume <= 0) { setError("Tidak dapat menghitung volume. Periksa nilai yang dimasukkan."); setResult(null); return; }

      const ratio = newVolume / origVolume;
      let timeNote = "";
      if (ratio > 1.5) timeNote = "Resep baru jauh lebih besar — tambahkan 10-20 menit waktu memanggang dan pantau kue";
      else if (ratio > 1.1) timeNote = "Loyang baru sedikit lebih besar — tambahkan 5-10 menit waktu memanggang";
      else if (ratio < 0.7) timeNote = "Loyang baru jauh lebih kecil — kurangi waktu memanggang sekitar 10-15 menit";
      else if (ratio < 0.9) timeNote = "Loyang baru sedikit lebih kecil — kurangi waktu memanggang sekitar 5 menit";
      else timeNote = "Ukuran loyang sangat mirip — waktu memanggang bisa dipertahankan hampir sama";

      setResult({
        ratio, origVolume, newVolume,
        origShapeName: PAN_SHAPES[origShape], newShapeName: PAN_SHAPES[newShape], timeNote,
        origDesc: origShape === "rectangular" ? `${oD1} × ${oD2} cm, kedalaman ${oDepth} cm` : `${oD1} cm, kedalaman ${oDepth} cm`,
        newDesc: newShape === "rectangular" ? `${nD1} × ${nD2} cm, kedalaman ${nDepth} cm` : `${nD1} cm, kedalaman ${nDepth} cm`,
      });
    } catch (err) {
      setError("Kesalahan perhitungan: " + err.message);
      setResult(null);
    }
  };

  useEffect(() => {
    if (origD1 && origDepth && newD1 && newDepth) { calculateResult(); }
    else { setResult(null); setError(""); }
  }, [origShape, origD1, origD2, origDepth, newShape, newD1, newD2, newDepth]);

  const handleClear = () => {
    setOrigShape("round"); setOrigD1(""); setOrigD2(""); setOrigDepth("");
    setNewShape("square"); setNewD1(""); setNewD2(""); setNewDepth("");
    setResult(null); setError("");
  };

  const renderOrigInputs = () => (
    <div className="row">
      <div className={`col-sm-12 ${needsD2(origShape) ? "col-md-4" : "col-md-6"}`}>
        <label>{getD1Label(origShape)}</label>
        <input type="number" className="form-control" value={origD1} onChange={(e) => setOrigD1(e.target.value)} min="0" step="0.5" placeholder={getD1Label(origShape)} />
      </div>
      {needsD2(origShape) && (
        <div className="col-sm-12 col-md-4">
          <label>Lebar (cm)</label>
          <input type="number" className="form-control" value={origD2} onChange={(e) => setOrigD2(e.target.value)} min="0" step="0.5" placeholder="Lebar" />
        </div>
      )}
      <div className={`col-sm-12 ${needsD2(origShape) ? "col-md-4" : "col-md-6"}`}>
        <label>Kedalaman / Tinggi (cm)</label>
        <input type="number" className="form-control" value={origDepth} onChange={(e) => setOrigDepth(e.target.value)} min="0" step="0.5" placeholder="Kedalaman" />
      </div>
    </div>
  );

  const renderNewInputs = () => (
    <div className="row">
      <div className={`col-sm-12 ${needsD2(newShape) ? "col-md-4" : "col-md-6"}`}>
        <label>{getD1Label(newShape)}</label>
        <input type="number" className="form-control" value={newD1} onChange={(e) => setNewD1(e.target.value)} min="0" step="0.5" placeholder={getD1Label(newShape)} />
      </div>
      {needsD2(newShape) && (
        <div className="col-sm-12 col-md-4">
          <label>Lebar (cm)</label>
          <input type="number" className="form-control" value={newD2} onChange={(e) => setNewD2(e.target.value)} min="0" step="0.5" placeholder="Lebar" />
        </div>
      )}
      <div className={`col-sm-12 ${needsD2(newShape) ? "col-md-4" : "col-md-6"}`}>
        <label>Kedalaman / Tinggi (cm)</label>
        <input type="number" className="form-control" value={newDepth} onChange={(e) => setNewDepth(e.target.value)} min="0" step="0.5" placeholder="Kedalaman" />
      </div>
    </div>
  );

  return (
    <div className="ltr" style={{ direction: "ltr" }}>
      <SharedHelmet TITLE={TITLE} DESCRIPTION={DESCRIPTION} KEYWORDS={KEYWORDS} OG_URL={OG_URL} SCHEMA={SCHEMA} />
      <div className="row"><div className="col-12 col-md-8"><FirstAds /></div></div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ padding: "15px", borderRadius: "5px" }}>
            <h1 style={{ fontSize: "22px", margin: 0 }}>Kalkulator Konversi Ukuran Loyang Kue</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Kalkulator Konversi Loyang Kue</strong> memecahkan masalah setiap orang yang suka memanggang: bagaimana jika resep saya ditulis untuk loyang 20 cm bulat tapi saya hanya punya loyang persegi panjang? Masukkan dimensi loyang asli dan baru, lalu Anda langsung mendapatkan <strong>rasio penyesuaian bahan</strong> dengan tepat. Tanpa tebakan, tanpa kue yang gagal.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Konversi Resep Anda Antar Loyang dengan Mudah</h2>
              </div>

              <div style={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "8px", padding: "15px", marginBottom: "15px" }}>
                <h3 style={{ fontSize: "16px", marginBottom: "12px", color: "#1a5f3f" }}>🎂 Loyang Asli (Loyang Resep)</h3>
                <div className="row">
                  <div className="col-sm-12">
                    <label>Bentuk Loyang</label>
                    <select className="form-select" value={origShape} onChange={(e) => { setOrigShape(e.target.value); setOrigD2(""); }}>
                      {Object.entries(PAN_SHAPES).map(([key, val]) => (<option key={key} value={key}>{val}</option>))}
                    </select>
                  </div>
                </div>
                {renderOrigInputs()}
              </div>

              <div style={{ backgroundColor: "#fff8f0", border: "1px solid #ffc98b", borderRadius: "8px", padding: "15px", marginBottom: "15px" }}>
                <h3 style={{ fontSize: "16px", marginBottom: "12px", color: "#8a4500" }}>🍰 Loyang Baru (Yang Akan Anda Gunakan)</h3>
                <div className="row">
                  <div className="col-sm-12">
                    <label>Bentuk Loyang</label>
                    <select className="form-select" value={newShape} onChange={(e) => { setNewShape(e.target.value); setNewD2(""); }}>
                      {Object.entries(PAN_SHAPES).map(([key, val]) => (<option key={key} value={key}>{val}</option>))}
                    </select>
                  </div>
                </div>
                {renderNewInputs()}
              </div>

              <div className="text-center mb-3 mt-4">
                <button onClick={handleClear} className="btn btn-secondary" style={{ fontSize: "16px", padding: "10px 30px" }}>Hapus</button>
              </div>

              {error && <div className="alert alert-warning" role="alert"><strong>Peringatan:</strong> {error}</div>}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}><strong>Hasil:</strong></h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Keterangan</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Nilai</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>Rasio Penyesuaian Jumlah</td>
                          <td style={{ textAlign: "center", fontSize: "22px", fontWeight: "bold", color: "#1a5f3f" }}>× {result.ratio.toFixed(2)}</td>
                        </tr>
                        <tr><td style={{ textAlign: "center" }}>Volume Loyang Asli</td><td style={{ textAlign: "center", fontSize: "16px" }}>{result.origVolume.toFixed(1)} cm³</td></tr>
                        <tr><td style={{ textAlign: "center" }}>Volume Loyang Baru</td><td style={{ textAlign: "center", fontSize: "16px" }}>{result.newVolume.toFixed(1)} cm³</td></tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Perbedaan Volume</td>
                          <td style={{ textAlign: "center", fontSize: "16px", color: result.ratio > 1 ? "#0f3d28" : "#8a0000" }}>
                            {result.ratio > 1 ? `+${((result.ratio - 1) * 100).toFixed(0)}%` : `${((result.ratio - 1) * 100).toFixed(0)}%`}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header"><strong>Cara Menerapkan Rasio?</strong></div>
                      <div className="info-section-body">
                        <p style={{ lineHeight: "1.8" }}>
                          Kalikan setiap bahan dalam resep asli Anda dengan faktor{" "}
                          <strong style={{ fontSize: "18px", color: "#1a5f3f" }}>{result.ratio.toFixed(2)}</strong>
                        </p>
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2.2" }}>
                          <li>🥚 <strong>Contoh — 3 telur:</strong> {(3 * result.ratio).toFixed(1)} telur</li>
                          <li>🧈 <strong>Contoh — 200 g mentega:</strong> {(200 * result.ratio).toFixed(0)} g</li>
                          <li>🍚 <strong>Contoh — 250 g tepung:</strong> {(250 * result.ratio).toFixed(0)} g</li>
                          <li>🍬 <strong>Contoh — 150 g gula:</strong> {(150 * result.ratio).toFixed(0)} g</li>
                        </ul>
                        <div className="alert alert-info mt-3" style={{ fontSize: "14px" }}>
                          <strong>💡 Catatan:</strong> Untuk telur, jika hasilnya pecahan seperti 2.4, gunakan 2 telur dan tambahkan sebagian telur ketiga yang sudah dikocok.
                        </div>
                      </div>
                    </div>
                    <div className="info-section">
                      <div className="info-section-header"><strong>Detail Perhitungan dan Tips Memanggang</strong></div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p><strong>Loyang Asli:</strong> {result.origShapeName} — {result.origDesc}</p>
                          <p><strong>Loyang Baru:</strong> {result.newShapeName} — {result.newDesc}</p>
                          <hr />
                          <p style={{ fontSize: "14px", color: "#555" }}>
                            <strong>Perhitungan:</strong> Rasio penyesuaian = volume loyang baru ÷ volume loyang asli<br />
                            = {result.newVolume.toFixed(1)} ÷ {result.origVolume.toFixed(1)} = <strong>{result.ratio.toFixed(3)}</strong>
                          </p>
                          <hr />
                          <div className="alert alert-warning mt-2" style={{ fontSize: "14px" }}>
                            <strong>⏱️ Waktu Memanggang:</strong> {result.timeNote}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row"><RelatedLinks /></div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds /><br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><strong>Apa itu Kalkulator Konversi Ukuran Loyang Kue?</strong></div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Ini adalah alat cerdas yang secara otomatis menghitung rasio penyesuaian bahan kue saat mengganti ukuran atau bentuk loyang. Idenya sederhana: setiap loyang memiliki volume (luas × kedalaman), dan ketika Anda beralih antara dua loyang yang berbeda, jumlah adonan yang dibutuhkan berubah dengan rasio yang sama.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>Cara Menggunakan Kalkulator:</h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih bentuk loyang asli (yang ada dalam resep) dan masukkan dimensinya</li>
                  <li>Pilih bentuk loyang baru yang akan Anda gunakan dan masukkan dimensinya</li>
                  <li>Hasilnya akan langsung muncul: faktor pengali yang diterapkan pada setiap bahan</li>
                  <li>Baca saran waktu memanggang di akhir hasil</li>
                </ol>
                <div className="alert alert-info mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>💡 Catatan:</strong> Kalkulator bekerja dengan loyang bulat, persegi, persegi panjang, bundt, dan springform. Masukkan dimensi dalam sentimeter untuk hasil yang paling akurat.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Perbandingan Luas Loyang Umum</strong></div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>Berikut tabel referensi cepat untuk loyang yang paling sering digunakan (kedalaman 5 cm):</p>
                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Jenis Loyang</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Dimensi</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Volume Perkiraan (cm³)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{ textAlign: "center" }}>Bulat</td><td style={{ textAlign: "center" }}>diameter 20 cm</td><td style={{ textAlign: "center" }}>1.571</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Bulat</td><td style={{ textAlign: "center" }}>diameter 23 cm</td><td style={{ textAlign: "center" }}>2.083</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Bulat</td><td style={{ textAlign: "center" }}>diameter 26 cm</td><td style={{ textAlign: "center" }}>2.659</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Persegi</td><td style={{ textAlign: "center" }}>20 × 20 cm</td><td style={{ textAlign: "center" }}>2.000</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Persegi Panjang</td><td style={{ textAlign: "center" }}>30 × 20 cm</td><td style={{ textAlign: "center" }}>3.000</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Persegi Panjang</td><td style={{ textAlign: "center" }}>33 × 23 cm</td><td style={{ textAlign: "center" }}>3.795</td></tr>
                  </tbody>
                </table>
                <div className="alert alert-success mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>✓ Tips:</strong> Loyang persegi 20 cm kira-kira setara dengan loyang bulat 23 cm dari segi volume — informasi yang sangat berguna di dapur.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Tips Konversi Loyang yang Berhasil</strong></div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>Tips Dasar:</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Ukur dimensi loyang dari dalam:</strong> Ukuran yang tertulis di loyang terkadang dari tepi luar</li>
                  <li><strong>Pantau kue lebih awal:</strong> Jika Anda memperbesar loyang, mulai periksa kematangan 10 menit sebelum waktu yang ditentukan</li>
                  <li><strong>Jangan isi loyang lebih dari dua pertiga:</strong> Adonan akan mengembang saat dipanggang</li>
                  <li><strong>Suhu tidak berubah:</strong> Hanya waktu memanggang yang terpengaruh oleh perubahan ukuran</li>
                </ul>
                <div className="alert alert-warning mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>⚠️ Peringatan:</strong> Menyesuaikan jumlah tidak selalu berarti rasa kue akan sempurna setiap saat. Beberapa resep — seperti muffin dan soufflé — sensitif terhadap penyesuaian dan membutuhkan percobaan sebelum acara penting.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CakePanCalculator;